import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { JobPosting } from '@/data/jobOptions';

export interface JobsSearchFilters {
  search?: string;
  city?: string;
  contract_type?: string;
  work_mode?: string;
  inclusivity_tags?: string[];
  page?: number;
  limit?: number;
  sort?: 'recent' | 'salary_asc' | 'salary_desc';
}

export function useJobsSearch(filters: JobsSearchFilters = {}) {
  const { search, city, contract_type, work_mode, inclusivity_tags, page = 1, limit = 20, sort = 'recent' } = filters;

  return useQuery({
    queryKey: ['jobs', filters],
    queryFn: async () => {
      let query = supabase
        .from('job_postings')
        .select(`
          *,
          companies:company_id (
            id,
            name,
            slug,
            logo_url,
            is_verified
          )
        `, { count: 'exact' })
        .eq('status', 'active')
        .gt('expires_at', new Date().toISOString());

      // Apply filters
      if (search) {
        query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
      }

      if (city) {
        query = query.eq('location_city', city);
      }

      if (contract_type) {
        query = query.eq('contract_type', contract_type as any);
      }

      if (work_mode) {
        query = query.eq('work_mode', work_mode as any);
      }

      if (inclusivity_tags && inclusivity_tags.length > 0) {
        query = query.overlaps('inclusivity_tags', inclusivity_tags);
      }

      // Sorting
      switch (sort) {
        case 'salary_asc':
          query = query.order('salary_range_min', { ascending: true, nullsFirst: false });
          break;
        case 'salary_desc':
          query = query.order('salary_range_max', { ascending: false, nullsFirst: false });
          break;
        default:
          query = query.order('published_at', { ascending: false });
      }

      // Pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        jobs: data as JobPosting[],
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / limit),
        currentPage: page,
      };
    },
    staleTime: 1000 * 60, // 1 minute
  });
}

export function useJobCities() {
  return useQuery({
    queryKey: ['job-cities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_postings')
        .select('location_city')
        .eq('status', 'active')
        .gt('expires_at', new Date().toISOString());

      if (error) throw error;

      // Get unique cities
      const cities = [...new Set(data.map(j => j.location_city))].sort();
      return cities;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
